import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Loader2, KeySquare, Undo2, FlaskConical, AlertTriangle, CheckCircle2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
const n = (v) => (v || 0).toLocaleString();
const money = (v) => `$${Math.round(v || 0).toLocaleString()}`;

const DEFAULT_RESIDUE_REASON =
  'Platform test binds left on the live book by the marine and crop class-pack test suites — one demo entity, staff-authored, no wording pinned. Not insureds. Owner ruling 19 Sep 2026: disposition as test artifacts.';

function Stat({ label, value, color, testid }) {
  return (
    <div className="px-3 py-2 rounded-sm" style={{ background: 'rgba(136,150,168,0.07)' }} data-testid={testid}>
      <p className="text-xl font-bold font-mono leading-none" style={{ color: color || C.navy }}>{value}</p>
      <p className="text-[10px] uppercase tracking-wider mt-1" style={{ color: C.slate }}>{label}</p>
    </div>
  );
}

export const ClassKeyCleanup = ({ api, isAdmin }) => {
  const [plan, setPlan] = useState(null);
  const [residue, setResidue] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('Normalise drifted class keys off the platform register — owner instruction 19 Sep 2026');
  const [residueReason, setResidueReason] = useState(DEFAULT_RESIDUE_REASON);

  const load = useCallback(() => {
    api.get('/migration/class-keys/plan').then(r => setPlan(r.data)).catch(() => {});
    api.get('/migration/qa-residue/scan').then(r => setResidue(r.data)).catch(() => {});
    api.get('/migration/class-keys/runs?limit=10').then(r => setRuns(r.data.runs || [])).catch(() => {});
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const apply = async () => {
    setBusy('apply');
    try {
      const { data } = await api.post('/migration/class-keys/apply', { note });
      toast.success(`${data.records} policy key(s) normalised — rollback kept on run ${data.run_id.slice(0, 8)}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The normalisation pass did not run');
    } finally { setBusy(''); }
  };

  const rollback = async (run) => {
    setBusy(run.run_id);
    const path = run.kind === 'qa_residue_disposition' ? 'qa-residue/rollback' : 'class-keys/rollback';
    try {
      const { data } = await api.post(`/migration/${path}/${run.run_id}`, { reason: 'Rolled back from the Migration Toolkit' });
      toast.success(`${data.restored} record(s) restored exactly`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The rollback did not run');
    } finally { setBusy(''); }
  };

  const disposition = async () => {
    setBusy('residue');
    try {
      const { data } = await api.post('/migration/qa-residue/disposition', { reason: residueReason });
      toast.success(`${data.policies} policies, ${data.invoices} invoices and ${data.claims} claims off the live book`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The disposition did not run');
    } finally { setBusy(''); }
  };

  return (
    <div className="space-y-4" data-testid="class-key-cleanup">
      {/* ── class keys ── */}
      <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="class-keys-card">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <KeySquare className="w-4 h-4" style={{ color: C.blue }} />
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Class key normalisation</h3>
          {plan && (
            <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase" data-testid="class-keys-pending"
              style={{ background: plan.to_change ? '#FDF7EC' : '#E7F6EF', color: plan.to_change ? C.amber : C.green }}>
              {plan.to_change ? `${n(plan.to_change)} to normalise` : 'every key resolves'}
            </span>
          )}
        </div>
        <p className="text-[11px] mb-3" style={{ color: C.slate }}>
          A policy carries a class <em>key</em> (<span className="font-mono">sme_commercial</span>) and a display{' '}
          <em>label</em> ("SME Commercial Package"). Some paths wrote the label into both. Nothing about the cover
          changes here — but a policy whose key is a label is invisible to every class-keyed gate, rate lookup and
          sweep, which is how three $0 policies stayed hidden.
        </p>

        {!plan ? (
          <p className="text-[11px]" style={{ color: C.slate }} data-testid="class-keys-loading">Reading the book…</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              <Stat label="Candidates read" value={n(plan.candidates)} testid="ck-candidates" />
              <Stat label="Will change" value={n(plan.to_change)} color={C.blue} testid="ck-to-change" />
              <Stat label="Live book" value={n(plan.live)} color={C.navy} testid="ck-live" />
              <Stat label="Rehearsal" value={n(plan.rehearsal)} color={C.slate} testid="ck-rehearsal" />
              <Stat label="Exceptions" value={n(plan.exceptions.reduce((a, e) => a + e.records, 0))}
                color={plan.exceptions.length ? C.amber : C.green} testid="ck-exceptions" />
            </div>

            {plan.changes.length > 0 && (
              <div className="space-y-1 mb-4" data-testid="ck-changes">
                {plan.changes.map((c) => (
                  <div key={`${c.from}-${c.to}`} className="flex flex-wrap items-center gap-2 py-1.5 px-2 rounded-sm text-[11px]"
                    style={{ background: 'rgba(26,76,124,0.04)' }} data-testid={`ck-change-${c.to}`}>
                    <span className="font-mono" style={{ color: C.slate }}>{c.from || '(blank)'}</span>
                    <ArrowRight className="w-3 h-3" style={{ color: C.slate }} />
                    <span className="font-mono font-semibold" style={{ color: C.navy }}>{c.to}</span>
                    <span style={{ color: C.slate }}>{c.to_name}</span>
                    <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase"
                      style={{ background: c.how === 'alias' ? '#FDF7EC' : 'rgba(136,150,168,0.15)', color: c.how === 'alias' ? C.amber : C.slate }}>
                      {c.how}
                    </span>
                    <span className="ml-auto font-mono" style={{ color: C.navy }}>{n(c.records)}</span>
                    <span className="text-[10px]" style={{ color: C.slate }}>
                      {c.live} live · {c.rehearsal} rehearsal · {money(c.gwp)}
                    </span>
                    {c.basis && <p className="w-full text-[10px] pl-1" style={{ color: C.amber }}>{c.basis}</p>}
                  </div>
                ))}
              </div>
            )}

            {plan.exceptions.length > 0 && (
              <div className="mb-4 p-3 rounded-sm" style={{ background: '#FDF7EC', borderLeft: `3px solid ${C.amber}` }}
                data-testid="ck-exception-list">
                <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: C.amber }}>
                  Left alone — the register cannot resolve these, so they are not guessed at
                </p>
                {plan.exceptions.map((e) => (
                  <p key={e.label} className="text-[11px]" style={{ color: C.slate }}>
                    <span className="font-mono" style={{ color: C.navy }}>{e.label}</span> · {n(e.records)} record(s)
                    ({e.live} live, {e.rehearsal} rehearsal) · {e.reason} · e.g. {e.samples.join(', ')}
                  </p>
                ))}
              </div>
            )}

            <p className="text-[10px] mb-3" style={{ color: C.slate }}>{plan.note}</p>

            {isAdmin && plan.to_change > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this pass is running"
                  className="h-8 text-[11px] rounded-sm flex-1 min-w-[260px]" data-testid="ck-note-input" />
                <button onClick={apply} disabled={busy === 'apply'}
                  className="px-3 py-1.5 rounded-sm text-[11px] font-semibold text-white disabled:opacity-50 flex items-center gap-1.5"
                  style={{ background: C.blue }} data-testid="ck-apply-btn">
                  {busy === 'apply' ? <Loader2 className="w-3 h-3 animate-spin" /> : <KeySquare className="w-3 h-3" />}
                  Normalise {n(plan.to_change)} key(s)
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* ── QA residue ── */}
      <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="qa-residue-card">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <FlaskConical className="w-4 h-4" style={{ color: C.red }} />
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Platform test residue on the live book</h3>
          {residue && (
            <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase" data-testid="residue-count"
              style={{ background: residue.records ? '#FDF1F1' : '#E7F6EF', color: residue.records ? C.red : C.green }}>
              {residue.records ? `${n(residue.records)} policies · ${money(residue.gwp)}` : 'live book clean'}
            </span>
          )}
        </div>
        {!residue ? (
          <p className="text-[11px]" style={{ color: C.slate }} data-testid="residue-loading">Scanning…</p>
        ) : residue.records === 0 ? (
          <p className="text-[11px] flex items-center gap-1.5" style={{ color: C.green }} data-testid="residue-empty">
            <CheckCircle2 className="w-3.5 h-3.5" />
            No test binds left on the live book. Every active policy with no wording pinned has been dispositioned.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-3">
              <Stat label="Policies" value={n(residue.records)} color={C.red} testid="residue-policies" />
              <Stat label="Recorded premium" value={money(residue.gwp)} color={C.red} testid="residue-gwp" />
              <Stat label="Invoices" value={n(residue.attached.invoices)} testid="residue-invoices" />
              <Stat label="Claims" value={n(residue.attached.claims)} testid="residue-claims" />
              <Stat label="Declarations" value={n(residue.attached.marine_declarations)} testid="residue-declarations" />
            </div>
            <p className="text-[11px] mb-1" style={{ color: C.navy }}>
              All on one entity — <span className="font-semibold">{residue.entity.legal_name || residue.entity.entity_id}</span> —
              authored by {residue.authors.filter(Boolean).join(', ') || 'platform staff'}, bound{' '}
              {String(residue.created.from).slice(0, 10)} to {String(residue.created.to).slice(0, 10)}.
            </p>
            <p className="text-[11px] mb-3" style={{ color: C.slate }}>{residue.note}</p>
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="residue-classes">
              {residue.by_class.map((c) => (
                <span key={c.class} className="px-2 py-1 rounded-sm text-[10px] font-mono"
                  style={{ background: 'rgba(226,75,74,0.07)', color: C.red }}>
                  {c.class} · {c.records} · {money(c.gwp)}
                </span>
              ))}
            </div>
            {isAdmin && (
              <div className="flex flex-wrap items-center gap-2">
                <Input value={residueReason} onChange={(e) => setResidueReason(e.target.value)}
                  className="h-8 text-[11px] rounded-sm flex-1 min-w-[280px]" data-testid="residue-reason-input" />
                <button onClick={disposition} disabled={busy === 'residue' || residueReason.trim().length < 10}
                  className="px-3 py-1.5 rounded-sm text-[11px] font-semibold text-white disabled:opacity-50 flex items-center gap-1.5"
                  style={{ background: C.red }} data-testid="residue-disposition-btn">
                  {busy === 'residue' ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
                  Disposition as test artifacts
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* ── runs ── */}
      {runs.length > 0 && (
        <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="ck-runs-card">
          <h3 className="text-sm font-semibold mb-3" style={{ color: C.navy }}>Cleanup runs</h3>
          <div className="space-y-1">
            {runs.map((r) => (
              <div key={r.run_id} className="flex flex-wrap items-center gap-3 py-1.5 border-t text-[11px]"
                style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`ck-run-${r.run_id}`}>
                <span className="font-mono" style={{ color: C.slate }}>{String(r.created_at).slice(0, 16).replace('T', ' ')}</span>
                <span style={{ color: C.navy }}>
                  {r.kind === 'qa_residue_disposition' ? 'Test residue dispositioned' : 'Class keys normalised'}
                </span>
                <span className="font-mono" style={{ color: C.navy }}>{n(r.records)} record(s)</span>
                {r.kind === 'qa_residue_disposition' && (
                  <span style={{ color: C.slate }}>+{n(r.invoices)} invoices · {n(r.claims)} claims</span>
                )}
                <span style={{ color: C.slate }}>by {r.by || 'unknown'}</span>
                {r.rolled_back ? (
                  <span className="px-2 py-0.5 rounded-sm text-[9px] font-bold uppercase"
                    style={{ background: 'rgba(136,150,168,0.15)', color: C.slate }} data-testid={`ck-run-rolledback-${r.run_id}`}>
                    rolled back
                  </span>
                ) : isAdmin && (
                  <button onClick={() => rollback(r)} disabled={busy === r.run_id}
                    className="ml-auto px-2 py-0.5 rounded-sm text-[10px] font-semibold border flex items-center gap-1 disabled:opacity-50"
                    style={{ borderColor: C.red, color: C.red }} data-testid={`ck-rollback-${r.run_id}`}>
                    {busy === r.run_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />}
                    Roll back
                  </button>
                )}
              </div>
            ))}
          </div>
          <p className="text-[10px] mt-3" style={{ color: C.slate }}>
            Every run keeps the before value of every field it touched, so a rollback restores the book row for row —
            including putting a field back to not existing at all.
          </p>
        </Card>
      )}
    </div>
  );
};
